import { Layout, Typography, Form, Input, Button, Select, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { CloseOutlined } from '@ant-design/icons';
import { ChangeEvent, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import auth from 'api/auth';
import classes from './Reset.module.scss';
import PasswordRule from 'routes/SignUp/extraRules/PasswordRule';
import { languagesValues } from 'routes/Main/Main';
import axios from 'axios';
import { validateActivateLink } from 'helpers/validateActivateLinks';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { setIsOpenChangePassword } from 'store/slices/windowStateSlice';

const { Title, Paragraph } = Typography;

const authType = window.REACT_APP_AUTH_TYPE !== 'REPLACE_REACT_APP_AUTH_TYPE' ? window.REACT_APP_AUTH_TYPE : process.env.REACT_APP_AUTH_TYPE; 
const loginAdress = authType === 'blitz' ? 'blitz-auth' : 'login';

type ResetPasswordProps = {
  isModal?: boolean;
}

export default function ResetPassword({isModal}: ResetPasswordProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [passwordTooltipOpen, setpasswordTooltipOpen] = useState(false);
  const [passwordRules, setPasswordRules] = useState<Record<string, 'error' | 'success'>>({
    min: 'error',
    latin: 'error',
    special: 'error',
    number: 'error',
    letter: 'error',
    noSpaces: 'error',
  });
  
  const { messageApi } = useAppSelector((store) => store.windowState);  

  const dispatch = useAppDispatch();

  const [form] = Form.useForm();
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const userEmail = searchParams.get('email') || localStorage.getItem('userEmail_rp') || '';
  const navigate = useNavigate();
  const defaultLanguage = localStorage.getItem('language') || 'en';
  const passwordRuleKeys = Object.keys(passwordRules);
  const link_id = searchParams.get('link_id') || localStorage.getItem('link_id_rp') || '';

  const modalCloseFunc = () => {
    dispatch(setIsOpenChangePassword(false));
    localStorage.removeItem('link_id_rp');
    localStorage.removeItem('userEmail_rp');
    localStorage.removeItem('user_id_rp');
  };

  useEffect(() => {
    validateActivateLink(link_id, t, navigate, true, isModal ? modalCloseFunc : undefined, messageApi);
    return () => {
      dispatch(setIsOpenChangePassword(false));
    }
  }, []);
  
  const onResetHandle = async (values: { password: string; confirm: string }) => {
    try {
      setIsLoading(true);
      const user_idBasex64 = searchParams.get('user') || '';
      let user_id = ''
      if (user_id) {
        user_id = atob(user_idBasex64);
      } else {
        user_id = localStorage.getItem('user_id_rp') || '';
      }
      await auth.restorePassword({ user_id, password: values.password, link_id });
      messageApi?.success(t('resetPassword.success'));
      isModal
        ? modalCloseFunc()
        : navigate(`/${loginAdress}`);
    } catch (err) {
      console.log(err)
      if (
        axios.isAxiosError(err) &&
        err.response?.data?.detail.message === 'New password cannot be the same as the old password'
      ) {
        return messageApi?.error(t('resetPassword.same'));
      }
      messageApi?.error(t('resetPassword.error'));
    } finally {
      setIsLoading(false);
    }
  };

  const onLanguageChange = (value: string) => {
    localStorage.setItem('language', value);
    i18n.changeLanguage(value);
  };

  const onPasswordChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    const updatedRules: Record<string, 'error' | 'success'> = {
      min: value.length >= 8 ? 'success' : 'error',
      latin: /[a-zA-Z]/.test(value) ? 'success' : 'error',
      special: /[^\w\d\sа-яА-ЯёЁ]/.test(value) ? 'success' : 'error',
      number: /\d/.test(value) ? 'success' : 'error',
      //@ts-ignore
      letter: /\p{L}/u.test(value) ? 'success' : 'error',
      noSpaces: !/\s/.test(value) ? 'success' : 'error',
    };

    setPasswordRules(updatedRules);
  };

  const onPasswordTooltipClose = () => setpasswordTooltipOpen(false);
  const onPasswordTooltipOpen = () => setpasswordTooltipOpen(true);

  const formElement = (<Form className={classes.form} onFinish={onResetHandle} form={form}>
    <Tooltip
      color='#fff'
      trigger='focus'
      classNames={{ root: classes.overlay }}
      open={passwordTooltipOpen}
      title={
        <div className={classes.overlay_container}>
          <CloseOutlined className={classes.overlay_close} onClick={onPasswordTooltipClose} />
          {passwordRuleKeys.map((key) => (
            <PasswordRule key={key} rule={key} type={passwordRules[key]} />
          ))}
        </div>
      }
    >
      <Form.Item
        name='password'
        rules={[
          {
            required: true,
            message: `${t('PersonalInfo.input')}"${t('PersonalInfo.password')}"`,
          },
          {
            pattern: /^(?=.*\d)(?=.*[a-zA-Z])(?=.*\W)(?!.*\s).{8,}$/,
            message: '',
          },
        ]}
      >
        <Input.Password
          placeholder={t('PersonalInfo.password')}
          size='large'
          className={classes.input}
          onFocus={onPasswordTooltipOpen}
          onBlur={onPasswordTooltipClose}
          onChange={onPasswordChange}
        />
      </Form.Item>
    </Tooltip>

    <Form.Item
      name='confirm'
      rules={[
        { required: true, message: `${t('PersonalInfo.confirm')}` },
        ({ getFieldValue }) => ({
          validator(_, value) {
            if (!value || getFieldValue('password') === value) {
              return Promise.resolve();
            }
            return Promise.reject(new Error(t('PersonalInfo.confirmText')));
          },
        }),
      ]}
    >
      <Input.Password
        placeholder={t('PersonalInfo.confirm')}
        size='large'
        className={classes.input}
      />
    </Form.Item>

    <Form.Item shouldUpdate>
      {() => (
        <Button
          type='primary'
          htmlType='submit'
          loading={isLoading}
          disabled={
            !form.isFieldsTouched(true) || !!form.getFieldsError().filter(({ errors }) => errors.length).length
          }
        >
          {t('resetPassword.submit')}
        </Button>
      )}
    </Form.Item>
  </Form>)

  return ( isModal
    ? <div style={{paddingBottom: 1}}>
      <div className={classes.typography}>
        <Typography>
          <Title level={3}>{t('resetPassword.title')}</Title>
          <Paragraph className={classes.subTitle}>
            {t('login.resetPassword')} {userEmail}
          </Paragraph>
        </Typography>
      </div>
      
      {formElement}
    </div>
    : <div className={classes.mainWrap}>
      <Layout className={classes.subElem}>
        <Select
          className={classes.localizationSelect}
          defaultValue={defaultLanguage}
          onChange={onLanguageChange}
          style={{ minWidth: 130 }}
          options={languagesValues}
          optionLabelProp={'label'}
        />
        <div className={classes.card}>
          <div className={classes.typography}>
            <Typography>
              <Title level={3}>{t('resetPassword.title')}</Title>
              <Paragraph className={classes.subTitle}>
                {t('login.resetPassword')} {userEmail}
              </Paragraph>
            </Typography>
          </div>

          {formElement}

        </div>
      </Layout>
    </div>
  );
}
